import { useTranslation } from 'react-i18next'
import {
  OccupantsFieldView,
  type OccupantsFieldProps,
} from '@/features/reservation/components/OccupantsFieldView'
import type { OccupantInput } from '@/features/reservation/schemas/wizardOccupants.schema'
import { HorizonGuestStepper } from './HorizonGuestStepper'

function adhocOccupants(occupants: OccupantInput[]): OccupantInput[] {
  return occupants.filter((o) => o.source === 'adhoc')
}

function beneficiaryOccupants(occupants: OccupantInput[]): OccupantInput[] {
  return occupants.filter((o) => o.source === 'beneficiary')
}

/**
 * Horizon wizard occupants: GuestStepper for party size (+ employee) above the default picker.
 */
export function OccupantsField(props: OccupantsFieldProps) {
  const { t } = useTranslation()
  const { occupants, capacityMax, onChange } = props
  const partySize = occupants.length + 1
  const maxParty = capacityMax === null ? undefined : capacityMax + 1

  const setPartySize = (nextPartySize: number) => {
    const companionsTarget = Math.max(0, nextPartySize - 1)
    if (capacityMax !== null && companionsTarget > capacityMax) {
      return
    }

    const beneficiaries = beneficiaryOccupants(occupants)
    const adhoc = adhocOccupants(occupants)
    const currentCompanions = occupants.length

    if (companionsTarget === currentCompanions) {
      return
    }

    if (companionsTarget < currentCompanions) {
      const removeCount = currentCompanions - companionsTarget
      if (adhoc.length >= removeCount) {
        onChange([...beneficiaries, ...adhoc.slice(0, adhoc.length - removeCount)])
        return
      }
      const remainingRemove = removeCount - adhoc.length
      onChange(beneficiaries.slice(0, Math.max(0, beneficiaries.length - remainingRemove)))
      return
    }

    const toAdd = companionsTarget - currentCompanions
    const placeholders: OccupantInput[] = Array.from({ length: toAdd }, (_, index) => ({
      source: 'adhoc' as const,
      first_name: t('horizon.wizard.guest_placeholder_first', { n: adhoc.length + index + 1 }),
      last_name: t('horizon.wizard.guest_placeholder_last'),
    }))
    onChange([...occupants, ...placeholders])
  }

  return (
    <div className="flex flex-col gap-4">
      <HorizonGuestStepper
        label={t('horizon.wizard.party_size')}
        value={partySize}
        min={1}
        max={maxParty}
        decrementLabel={t('horizon.wizard.party_decrement')}
        incrementLabel={t('horizon.wizard.party_increment')}
        onChange={setPartySize}
      />
      <p className="text-sm text-[var(--brand-text-secondary)]">{t('horizon.wizard.party_and_form_hint')}</p>
      <OccupantsFieldView {...props} />
    </div>
  )
}
